import { supabase } from './supabase'

// ═══════════════════════════════════════════════════════════
//  UTILITAIRES
// ═══════════════════════════════════════════════════════════

// Identifiant unique de cet appareil (pour le blocage PIN et l'idempotence)
export function getDeviceId() {
  let id = localStorage.getItem('stocko_device_id')
  if (!id) {
    id = 'dev_' + Math.random().toString(36).slice(2) + Date.now().toString(36)
    localStorage.setItem('stocko_device_id', id)
  }
  return id
}

// Clé d'idempotence : évite les doublons lors des re-tentatives réseau
function nouveauClientUid() {
  return getDeviceId() + '_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8)
}

// Arrondi propre pour les quantités décimales (évite 2.9999999999)
export function arrondiQty(n) {
  return Math.round(parseFloat(n) * 100) / 100
}

// ═══════════════════════════════════════════════════════════
//  PRODUITS
// ═══════════════════════════════════════════════════════════

export async function getProduits(boutiqueId) {
  const { data, error } = await supabase
    .from('produits')
    .select('*')
    .eq('boutique_id', boutiqueId)
    .order('nom')
  return { data, error }
}

export async function addProduit(boutiqueId, produit) {
  const { data, error } = await supabase
    .from('produits')
    .insert({ ...produit, boutique_id: boutiqueId })
    .select()
    .single()
  return { data, error }
}

export async function updateProduit(id, updates) {
  const { data, error } = await supabase
    .from('produits')
    .update(updates)
    .eq('id', id)
    .select()
    .single()
  return { data, error }
}

export async function deleteProduit(id) {
  const { error } = await supabase.from('produits').delete().eq('id', id)
  return { error }
}

// ═══════════════════════════════════════════════════════════
//  MOUVEMENTS — VERSION ATOMIQUE
//  Tout passe par la fonction PostgreSQL : plus de race condition,
//  plus de stock modifié sans trace.
// ═══════════════════════════════════════════════════════════

export async function enregistrerMouvement(boutiqueId, { produitId, type, quantite, note, clientUid }) {
  const { data, error } = await supabase.rpc('enregistrer_mouvement_atomique', {
    p_boutique_id: boutiqueId,
    p_produit_id:  produitId,
    p_type:        type,
    p_quantite:    arrondiQty(quantite),
    p_note:        note || null,
    p_client_uid:  clientUid || nouveauClientUid()
  })

  if (error) return { error }

  const res = data?.[0]
  if (res?.erreur) return { error: { message: res.erreur } }

  return {
    data: { id: res?.mouvement_id },
    nouvelleQty: res?.nouvelle_qty,
    error: null
  }
}

// Mouvement sans modification du stock (utilisé pour le stock initial d'un produit)
export async function enregistrerMouvementSansModif(boutiqueId, { produitId, produitNom, type, quantite, note, prixAchat = 0, prixUnitaire = 0 }) {
  const { data, error } = await supabase
    .from('mouvements')
    .insert({
      boutique_id: boutiqueId,
      produit_id: produitId,
      produit_nom: produitNom,
      type,
      quantite: arrondiQty(quantite),
      note: note || null,
      prix_achat: prixAchat,
      prix_unitaire: prixUnitaire,
      client_uid: nouveauClientUid()
    })
    .select()
    .single()
  return { data, error }
}

export async function annulerMouvement(boutiqueId, mouvement) {
  const { data, error } = await supabase.rpc('annuler_mouvement_atomique', {
    p_boutique_id:  boutiqueId,
    p_mouvement_id: mouvement.id
  })
  if (error) return { error }
  const res = data?.[0]
  if (!res?.succes) return { error: { message: res?.erreur || 'Annulation impossible' } }
  return { error: null }
}

// Historique : renvoie TOUT, annulations comprises, pour la tracabilite.
// Les colonnes annule / est_annulation servent a afficher un badge.
export async function getMouvements(boutiqueId, limite = 50) {
  const { data, error } = await supabase
    .from('mouvements')
    .select('*, produits(prix_unitaire, prix_achat, unite)')
    .eq('boutique_id', boutiqueId)
    .order('created_at', { ascending: false })
    .limit(limite)
  return { data, error }
}

export async function getMouvementsJour(boutiqueId, date) {
  const debut = new Date(date); debut.setHours(0, 0, 0, 0)
  const fin   = new Date(date); fin.setHours(23, 59, 59, 999)
  const { data, error } = await supabase
    .from('mouvements')
    .select('*, produits(prix_unitaire, prix_achat, unite)')
    .eq('boutique_id', boutiqueId)
    .gte('created_at', debut.toISOString())
    .lte('created_at', fin.toISOString())
    .order('created_at', { ascending: false })
  return { data, error }
}

// ═══════════════════════════════════════════════════════════
//  BILAN
// ═══════════════════════════════════════════════════════════

export async function getBilanFinancier(boutiqueId, periode = 30) {
  const debut = new Date()
  debut.setDate(debut.getDate() - periode)
  debut.setHours(0, 0, 0, 0)

  const { data, error } = await supabase
    .from('mouvements')
    .select('*, produits(prix_unitaire, prix_achat, unite)')
    .eq('boutique_id', boutiqueId)
    .gte('created_at', debut.toISOString())
    .eq('annule', false)           // exclut les mouvements annules
    .eq('est_annulation', false)   // exclut les mouvements d'annulation
    .order('created_at', { ascending: true })
  return { data, error }
}

export async function getMouvementsPeriode(boutiqueId, debut, fin) {
  const { data, error } = await supabase
    .from('mouvements')
    .select('*, produits(prix_unitaire, prix_achat, unite)')
    .eq('boutique_id', boutiqueId)
    .gte('created_at', debut.toISOString())
    .lte('created_at', fin.toISOString())
    .eq('annule', false)
    .eq('est_annulation', false)
    .order('created_at', { ascending: true })
  return { data, error }
}

// Compte les mouvements neutralises sur une periode.
// Sert a mentionner "N mouvement(s) annule(s)" sur le PDF, pour que
// le commercant ne croie pas qu'une operation a disparu sans raison.
export async function getAnnulationsPeriode(boutiqueId, debut, fin) {
  const { count, error } = await supabase
    .from('mouvements')
    .select('id', { count: 'exact', head: true })
    .eq('boutique_id', boutiqueId)
    .gte('created_at', debut.toISOString())
    .lte('created_at', fin.toISOString())
    .eq('annule', true)
  return { count: count || 0, error }
}

export async function getStatsJour(boutiqueId) {
  const debut = new Date(); debut.setHours(0, 0, 0, 0)

  const [{ data: mouvements, error: errMouvements }, { data: produits, error: errProduits }] = await Promise.all([
    supabase.from('mouvements')
      .select('*, produits(prix_unitaire, prix_achat, unite)')
      .eq('boutique_id', boutiqueId)
      .gte('created_at', debut.toISOString())
      .eq('annule', false)
      .eq('est_annulation', false)
      .order('created_at', { ascending: false }),
    supabase.from('produits').select('*').eq('boutique_id', boutiqueId)
  ])

  if (errMouvements || errProduits) {
    return { data: null, error: errMouvements || errProduits }
  }

  const movs  = mouvements || []
  const prods = produits || []
  const sorties = movs.filter(m => m.type === 'sortie')

  // ?? au lieu de || : un prix de 0 reste 0, il n'est pas remplacé
  const encaisse   = sorties.reduce((s, m) => s + m.quantite * (m.prix_unitaire ?? m.produits?.prix_unitaire ?? 0), 0)
  const coutVentes = sorties.reduce((s, m) => s + m.quantite * (m.prix_achat   ?? m.produits?.prix_achat   ?? 0), 0)

  return {
    data: {
      mouvements: movs,
      nbProduits: prods.length,
      stockFaible: prods.filter(p => p.quantite > 0 && p.quantite <= p.seuil_alerte).length,
      ruptures: prods.filter(p => p.quantite === 0).length,
      encaisse,
      benefice: encaisse - coutVentes,
      nbMouvements: movs.length
    },
    error: null
  }
}

// ═══════════════════════════════════════════════════════════
//  BOUTIQUE / ÉQUIPE
// ═══════════════════════════════════════════════════════════

export async function updateBoutique(id, updates) {
  const { data, error } = await supabase
    .from('boutiques').update(updates).eq('id', id).select().single()
  return { data, error }
}

export async function getEmployes(boutiqueId) {
  const { data, error } = await supabase
    .from('employes').select('*')
    .eq('boutique_id', boutiqueId)
    .order('created_at', { ascending: false })
  return { data, error }
}

export async function ajouterEmploye(boutiqueId, { nom, email, role }) {
  const { data, error } = await supabase
    .from('employes').insert({ boutique_id: boutiqueId, nom, email, role })
    .select().single()
  return { data, error }
}

export async function supprimerEmploye(id) {
  const { error } = await supabase.from('employes').delete().eq('id', id)
  return { error }
}

export async function updatePinCaissier(boutiqueId, pin) {
  const { data, error } = await supabase
    .from('boutiques')
    .update({ caissier_pin: pin, pin_actif: !!pin })
    .eq('id', boutiqueId).select().single()
  return { data, error }
}

export async function getBoutiqueCode(boutiqueId) {
  const { data, error } = await supabase
    .from('boutiques')
    .select('boutique_code, caissier_pin, pin_actif')
    .eq('id', boutiqueId).single()
  return { data, error }
}

export async function resetBoutique(boutiqueId) {
  await supabase.from('mouvements').delete().eq('boutique_id', boutiqueId)
  await supabase.from('produits').delete().eq('boutique_id', boutiqueId)
  const { error } = await supabase.from('paiements').delete().eq('boutique_id', boutiqueId)
  return { error }
}

// ═══════════════════════════════════════════════════════════
//  ABONNEMENTS
// ═══════════════════════════════════════════════════════════

export async function getAbonnement(boutiqueId) {
  const { data, error } = await supabase
    .from('abonnements').select('*').eq('boutique_id', boutiqueId).single()
  return { data, error }
}

export async function getPaiements(boutiqueId) {
  const { data, error } = await supabase
    .from('paiements').select('*')
    .eq('boutique_id', boutiqueId)
    .order('created_at', { ascending: false })
  return { data, error }
}

export async function soumettrePaiementManuel(boutiqueId, { montant, methode, reference, boutique_nom }) {
  const { data, error } = await supabase
    .from('paiements')
    .insert({
      boutique_id: boutiqueId,
      montant,
      statut: 'en_attente',
      methode,
      reference,
      note: `Paiement manuel - ${boutique_nom} - Ref: ${reference}`,
      mois_concerne: new Date().toISOString().slice(0, 7)
    })
    .select().single()
  return { data, error }
}

export function abonnementActif(abonnement) {
  if (!abonnement) return false
  return new Date(abonnement.date_fin) > new Date() && abonnement.statut !== 'expire'
}
